"use client";
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import useIsClient from "@/hooks/useIsClient";
import useShouldFire from "./useShouldFire";
export default function useFacebookPixel(
  pixel_id?: string | number,
  refresh?: boolean,
  advancedMatching?: any,
  pageViewEventId?: string,
) {
  const pathname = usePathname();
  const client = useIsClient();
  const shouldFire = useShouldFire();
  useEffect(() => {
    if (!pixel_id || !client || !shouldFire) return;
    if (pathname) {
      let ReactPixel: any;
      import("@/lib/react-facebook-pixel")
        .then((module) => (ReactPixel = module.default))
        .then(() => {
          if (!(window as any)._fbPixelInitialized) {
            (window as any)._fbPixelInitialized = new Set();
          }
          if (!(window as any)._fbPixelInitialized.has(pixel_id)) {
            const initAdvancedMatching: any = {};
            if (advancedMatching) {
              if (advancedMatching.email)
                initAdvancedMatching.em = advancedMatching.email;
              if (advancedMatching.email_hash)
                initAdvancedMatching.em = advancedMatching.email_hash;
              if (advancedMatching.phone)
                initAdvancedMatching.ph = advancedMatching.phone;
              if (advancedMatching.phone_hash)
                initAdvancedMatching.ph = advancedMatching.phone_hash;
              if (advancedMatching.external_id)
                initAdvancedMatching.external_id = advancedMatching.external_id;
              if (advancedMatching.first_name)
                initAdvancedMatching.fn = advancedMatching.first_name;
              if (advancedMatching.last_name)
                initAdvancedMatching.ln = advancedMatching.last_name;
              if (advancedMatching.fb_login_id)
                initAdvancedMatching.fb_login_id = advancedMatching.fb_login_id;
              if (advancedMatching.city_name)
                initAdvancedMatching.ct = advancedMatching.city_name;
              if (advancedMatching.area_name)
                initAdvancedMatching.ar = advancedMatching.area_name;
              if (advancedMatching.country_name)
                initAdvancedMatching.co = advancedMatching.country_name;
            }

            ReactPixel.init(pixel_id, initAdvancedMatching, {
              autoConfig: true,
            });
            (window as any)._fbPixelInitialized.add(pixel_id);
          }
          // Reuse the caller-supplied event_id (shared with the server-side
          // CAPI PageView) as the fbq dedup key.
          ReactPixel.pageView(pageViewEventId);
        });
    }
  }, [
    pathname,
    client,
    pixel_id,
    refresh,
    shouldFire,
    advancedMatching,
    pageViewEventId,
  ]);
  function trackEvent(event: any, data: any, eventID?: string) {
    let ReactPixel: any;
    import("@/lib/react-facebook-pixel")
      .then((module) => (ReactPixel = module.default))
      .then(() => {
        const customerInfo: any = {};

        if (data.email) customerInfo.em = data.email;
        if (data.email_hash) customerInfo.em = data.email_hash;
        if (data.phone) customerInfo.ph = data.phone;
        if (data.phone_hash) customerInfo.ph = data.phone_hash;
        if (data.external_id) customerInfo.external_id = data.external_id;
        if (data.client_ip_address)
          customerInfo.client_ip_address = data.client_ip_address;
        if (data.client_user_agent)
          customerInfo.client_user_agent = data.client_user_agent;
        if (data.first_name) customerInfo.fn = data.first_name;
        if (data.last_name) customerInfo.ln = data.last_name;

        const eventData = { ...data };
        // Remove customer PII — belongs in options (4th arg), not event data
        delete eventData.email;
        delete eventData.email_hash;
        delete eventData.phone;
        delete eventData.phone_hash;
        delete eventData.external_id;
        delete eventData.client_ip_address;
        delete eventData.client_user_agent;
        delete eventData.first_name;
        delete eventData.last_name;
        // Remove non-standard fields that Meta ignores client-side
        delete eventData.first_name_hash;
        delete eventData.last_name_hash;
        delete eventData.city;
        delete eventData.area;
        delete eventData.country;
        delete eventData.event_time;

        // ReactPixel.track() auto-generates eventID if not provided
        // and forwards all options (eventID + customer info) to fbq
        const resolvedEventID = ReactPixel.track(event, eventData, {
          eventID: eventID || undefined,
          ...customerInfo,
        });

        // Log Purchase events for debugging
        if ((event as string).toLocaleLowerCase() === "purchase") {
          const logDetails = {
            event_name: "Purchase",
            event_id: resolvedEventID,
            ...data,
            content_ids: Array.isArray(data.content_ids)
              ? data.content_ids.join(",")
              : data.content_ids,
          };

          fetch("/api/log-info", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              message: "Facebook Pixel Purchase Event",
              details: logDetails,
              host:
                typeof window !== "undefined"
                  ? window.location.hostname
                  : "next-server",
              url:
                typeof window !== "undefined" ? window.location.pathname : "",
            }),
          });
        }
      });
  }

  return { trackEvent };
}
